// LLM generated - Model Claude 4 Sonnet
import { IconDotsVertical, IconMoodPlus, IconFlag, IconTrash } from '@tabler/icons-react';
import { Menu, useMantineTheme } from '@mantine/core';
import useTranslation from 'next-translate/useTranslation';
import { useRef } from 'react';

import { Text } from '../../../Text';
import { Avatar } from '../../../Avatar';
import { Button } from '../../../Button';
import type { ChatBubbleProps } from './types';
import useStyles from './ChatBubble.styles';
import ReactionDropdown, { type ReactionDropdownRef } from './ReactionDropdown/ReactionDropdown';

function ChatBubble({
  message,
  onReportClick,
  onDeleteClick,
  onReactionSelect,
  onReactionRemove,
  availableReactions = [],
  isLoadingReactions = false,
  currentUserHandle,
  userReactions = [],
  ...props
}: ChatBubbleProps) {
  const { t } = useTranslation('chat');
  const theme = useMantineTheme();
  const reactionDropdownRef = useRef<ReactionDropdownRef>(null);

  // Destructure message properties
  const {
    id: messageId,
    avatar,
    handle,
    message: messageText,
    timestamp,
    reactions = [],
    reactionCount = 0,
    isCommissioner = false,
    variant = 'other',
  } = message;

  const { classes, cx } = useStyles({ variant });

  const handleReportClick = () => {
    onReportClick?.(handle, messageId);
  };

  const handleDeleteClick = () => {
    onDeleteClick?.(messageId);
  };

  const handleReactionSelect = (reactionCode: string) => {
    onReactionSelect?.(messageId, reactionCode);
  };

  const handleReactionRemove = (reactionCode: string) => {
    onReactionRemove?.(messageId, reactionCode);
  };

  return (
    <div className={classes.container} {...props}>
      {/* Avatar - only show for 'other' variant, not for current user's messages */}
      {variant === 'other' && (
        <div className={classes.avatar}>
          <Avatar src={avatar} handle={handle} size={24} radius="xl" />
        </div>
      )}

      {/* Chat Bubble */}
      <div className={classes.bubble}>
        {/* Header with handle and options */}
        <div className={classes.header}>
          <div className={classes.handleSection}>
            <Text variant="label-medium" className={classes.handle}>
              {handle}
            </Text>
          </div>
          {/* Options menu - Report for other users, Delete for current user */}
          <Menu shadow="md" width={200}>
            <Menu.Target>
              <div
                className={classes.optionsButton}
                aria-label={t('ariaLabels.messageOptions')}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    // Menu.Target will handle the click
                  }
                }}
              >
                <IconDotsVertical
                  size={16}
                  color={variant === 'current' ? theme.colors.gray2[1] : theme.colors.gray2[4]}
                />
              </div>
            </Menu.Target>

            <Menu.Dropdown>
              {variant === 'current' ? (
                <Menu.Item
                  icon={<IconTrash size={16} color={theme.colors.red[4]} />}
                  onClick={handleDeleteClick}
                >
                  <Text variant="subhead-medium" color={theme.colors.red[4]}>
                    {t('message.delete')}
                  </Text>
                </Menu.Item>
              ) : (
                <Menu.Item
                  icon={<IconFlag size={16} color={theme.colors.red[4]} />}
                  onClick={handleReportClick}
                >
                  <Text variant="subhead-medium" color={theme.colors.red[4]}>
                    {t('message.report')}
                  </Text>
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </div>

        {/* Message content */}
        <Text
          variant="title-medium"
          color={variant === 'current' ? theme.white : theme.colors.gray2[5]}
          className={classes.message}
        >
          {messageText}
        </Text>

        {/* Footer with reactions and timestamp */}
        <div className={classes.footer}>
          <div className={classes.reactions}>
            <ReactionDropdown
              ref={reactionDropdownRef}
              availableReactions={availableReactions}
              onReactionSelect={handleReactionSelect}
              onReactionRemove={handleReactionRemove}
              userReactions={userReactions}
              isLoading={isLoadingReactions}
              disabled={isLoadingReactions || availableReactions.length === 0}
              reactions={reactions}
              reactionCount={reactionCount}
              variant={variant}
            />
          </div>
          <Text variant="body-medium" className={classes.timestamp}>
            {timestamp}
          </Text>
        </div>
      </div>
    </div>
  );
}

export default ChatBubble;
